import { useEffect } from "react";

/**
 * A row on its way that holds the focus goes once its card lands and the list shows it: the focus moves to the row
 * beside it, else to the page's heading, rather than to the document.
 */
export function useFocusOutlivesRows(): void {
  useEffect(() => {
    const main = document.querySelector("main");
    if (main === null) return;
    let held: Element | null = null;
    const onFocus = (e: FocusEvent) => {
      held = e.target instanceof Element ? e.target.closest("[data-scanning-row]") : null;
    };
    document.addEventListener("focusin", onFocus);
    const observer = new MutationObserver((records) => {
      const gone = held;
      if (gone === null || gone.isConnected) return;
      held = null;
      if (document.activeElement !== null && document.activeElement !== document.body) return;
      const next = besideRow(gone, records, main) ?? main.querySelector<HTMLElement>("h1, h2");
      if (next === null) return;
      if (!next.hasAttribute("tabindex")) next.setAttribute("tabindex", "-1");
      next.focus({ preventScroll: true });
    });
    observer.observe(main, { childList: true, subtree: true });
    return () => {
      document.removeEventListener("focusin", onFocus);
      observer.disconnect();
    };
  }, []);
}

/** The row on its way beside one that went: a row the same commit took off is gone too, so the walk goes on past it. */
function besideRow(gone: Node, records: readonly MutationRecord[], main: HTMLElement): HTMLElement | null {
  const removal = (node: Node) =>
    records.find((r) => Array.from(r.removedNodes).some((n) => n === node || n.contains(node)));
  const record = removal(gone);
  if (!record) return null;
  for (const step of ["nextSibling", "previousSibling"] as const) {
    let n = record[step];
    for (let hops = 0; n !== null && !main.contains(n) && hops < records.length; hops++) n = removal(n)?.[step] ?? null;
    if (n instanceof HTMLElement && n.matches("[data-scanning-row]")) return n;
  }
  return null;
}
